import { MouseEvent, ReactNode } from 'react';
// Translation
import { useTranslation } from 'react-i18next';
// Components/ui
import { Box, Grid } from '@mui/material';
import Chip from '@mui/material/Chip';
import WarningIcon from '@mui/icons-material/Warning';
import ContactInfoPopup from '../../../../../../../../../../ContactInfoPopup';
import { style } from './style';
// Hooks
import { useContactInfoPopup } from './hooks/useContactInfoPopup';

// Codigo del warning de duplicado
const DUPLICATE_CONTACT_CODE = '100102';

type WarningProps = {
  text: string;
  code?: string;
  existingContactId?: string | null;
};

// Convierte la key de la traduccion en un link clickeable.
// Si no hay marcador, devuelve el texto plano.
const renderLabelWithLink = (
  text: string,
  onLinkClick: (e: MouseEvent<HTMLElement>) => void,
): ReactNode => {
  const match = text.match(/<0>(.*?)<\/0>/);
  if (!match) return text;

  const [full, linkText] = match;
  const [before, after] = text.split(full);

  return (
    <>
      {before}
      <Box
        component="span"
        onClick={onLinkClick}
        sx={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}
      >
        {linkText}
      </Box>
      {after}
    </>
  );
};

const Warning = ({ text, code, existingContactId }: WarningProps) => {
  const { i18n } = useTranslation();
  const { contactData, anchorEl, isOpen, openPopup, closePopup, navigate } = useContactInfoPopup(
    existingContactId ?? null,
  );

  const isDuplicate = code === DUPLICATE_CONTACT_CODE;
  const hasContact = Boolean(existingContactId);

  // Si es un warning de duplicado y hay un contacto existente, renderiza el label con link.
  const label = isDuplicate && hasContact ? renderLabelWithLink(text, openPopup) : text;

  return (
    <Grid item>
      <Box sx={style.secondaryContainer}>
        <Chip
          icon={<WarningIcon sx={style.icon} />}
          sx={style.chip}
          label={label}
          color="error"
          variant="outlined"
        />
      </Box>
      {isDuplicate && hasContact && (
        <ContactInfoPopup
          contactData={contactData ?? undefined}
          open={isOpen}
          anchorEl={anchorEl}
          onClose={closePopup}
          navigate={navigate}
          language={i18n.language.toLowerCase() as 'en' | 'es'}
          avatarImgUrl={contactData?.imageUrl}
        />
      )}
    </Grid>
  );
};

export default Warning;
